/**
 * Pure trial-status derivation helpers.
 *
 * Split from `useTrialStatus.ts` so unit tests can import the derivation
 * function without pulling the React + Supabase dependency chain. The hook
 * re-exports from here for backward compatibility.
 *
 * @module lib/trialStatus
 */
import type { SubscriptionDetails } from './billing';

/** Minimum trial length in days that still counts as "not yet self-extended". */
export const MAX_SELF_EXTEND_ELIGIBLE_DAYS = 15; // 14-day default + 1-day fudge for clock skew

/** Days-remaining threshold at or below which the chip switches to warning tint. */
export const ENDING_SOON_THRESHOLD_DAYS = 2;

/**
 * Days-remaining threshold below which the self-serve Extend button becomes
 * visible. The user shouldn't see "Extend 7 days" for the whole 14-day trial;
 * only in the final stretch when they're deciding whether to commit.
 */
export const SHOW_EXTEND_THRESHOLD_DAYS = 3;

const DAY_SECONDS = 86_400;

/** Subscription fields the derivation actually reads. Kept loose so callers
 *  can pass a richer `SubscriptionDetails` object without massaging. */
export interface TrialStatusSubscription extends Pick<SubscriptionDetails, 'status' | 'trial_start' | 'trial_end'> {}

/** Derived trial state. Pure function of ``SubscriptionDetails + now``. */
export interface TrialStatus {
  /** True when the subscription is in the Stripe ``trialing`` status and ends in the future. */
  isTrialing: boolean;
  /** Days left until ``trial_end``, rounded up, clamped at 0. */
  daysRemaining: number;
  /** Trial end as a Date, or null when no trial is active. */
  endsAt: Date | null;
  /**
   * Whether this trial is eligible for the one-time self-serve extension
   * based on trial length. False for sales-gifted trials longer than 14 days
   * or trials already extended.
   */
  canExtend: boolean;
  /**
   * Whether the "Extend 7 days" CTA should be rendered to the user. Only true
   * in the final stretch of the trial (<= 3 days remaining) AND the trial is
   * eligible - avoids showing the extend button for the entire 14-day window.
   */
  shouldShowExtendButton: boolean;
  /** True when ``daysRemaining <= 2``. Drives a warning tint in UI. */
  isEndingSoon: boolean;
  /**
   * True when a trial_end timestamp exists on the subscription, whether or
   * not we're currently within the trial window. Lets the billing page
   * distinguish "never trialed" from "trial ended" states.
   */
  hadTrial: boolean;
}

/**
 * Pure derivation of trial UI state from a Stripe subscription snapshot.
 *
 * @param subscription - Live subscription payload, or null when the org has none.
 * @param nowSeconds - Current Unix timestamp (seconds). Taken as a parameter
 *   so tests can pin deterministic times without faking the clock.
 */
export function deriveTrialStatus(
  subscription: TrialStatusSubscription | null,
  nowSeconds: number,
): TrialStatus {
  if (!subscription) {
    return {
      isTrialing: false,
      daysRemaining: 0,
      endsAt: null,
      canExtend: false,
      shouldShowExtendButton: false,
      isEndingSoon: false,
      hadTrial: false,
    };
  }

  const trialStart = subscription.trial_start ?? null;
  const trialEnd = subscription.trial_end ?? null;
  const hadTrial = trialEnd !== null;

  if (!hadTrial || trialEnd === null) {
    return {
      isTrialing: false,
      daysRemaining: 0,
      endsAt: null,
      canExtend: false,
      shouldShowExtendButton: false,
      isEndingSoon: false,
      hadTrial: false,
    };
  }

  const endsAt = new Date(trialEnd * 1000);
  const isTrialing = subscription.status === 'trialing' && trialEnd > nowSeconds;

  const remainingSeconds = Math.max(0, trialEnd - nowSeconds);
  const daysRemaining = Math.ceil(remainingSeconds / DAY_SECONDS);

  let canExtend = false;
  if (isTrialing && trialStart !== null) {
    const trialLengthDays = (trialEnd - trialStart) / DAY_SECONDS;
    canExtend = trialLengthDays <= MAX_SELF_EXTEND_ELIGIBLE_DAYS;
  }

  const isEndingSoon = isTrialing && daysRemaining <= ENDING_SOON_THRESHOLD_DAYS;
  const shouldShowExtendButton =
    canExtend && daysRemaining <= SHOW_EXTEND_THRESHOLD_DAYS;

  return {
    isTrialing,
    daysRemaining,
    endsAt,
    canExtend,
    shouldShowExtendButton,
    isEndingSoon,
    hadTrial,
  };
}
